/* First-party referral capture. Public pf_ref is not authentication.
   Canonical link: https://getpocketfactory.com/?pf_ref=<opaque-token>
   PROPOSED DEFAULT: 30-day last-valid-referral window in this browser.
   Clicks are diagnostic only. Reward amounts never come from this script. */
(function (root) {
  const TOKEN_RE = /^pfref_[a-f0-9]{32}$/;
  const KEY = "pf.ref.v1";
  const ATTR_V = "pf-attr-v1";
  const WINDOW_MS = 30 * 24 * 60 * 60 * 1000;
  // pocket-core.js names the Worker; the layout's data-referral-worker is the fallback if it never loaded.
  const workerOrigin = () => String(root.PocketCore?.workerBase?.() || root.document?.body?.dataset.referralWorker || "").replace(/\/$/, "");

  function readToken(search) {
    try {
      const token = new URLSearchParams(search || root.location.search).get("pf_ref");
      return TOKEN_RE.test(token || "") ? token : null;
    } catch (_) {
      return null;
    }
  }

  function load() {
    try {
      const raw = root.localStorage?.getItem(KEY);
      const saved = raw ? JSON.parse(raw) : null;
      if (!saved || !TOKEN_RE.test(saved.token || "")) return null;
      const captured = Date.parse(saved.capturedAt || "") || 0;
      if (captured && Date.now() - captured > WINDOW_MS) return null;
      return saved;
    } catch (_) {
      return null;
    }
  }

  function persist(state) {
    try { root.localStorage?.setItem(KEY, JSON.stringify(state)); } catch (_) {}
  }

  function setField(form, selector, value) {
    const input = form.querySelector(selector);
    if (input) input.value = value || "";
  }

  function applyToForms(state) {
    if (!state?.token) return;
    root.document.querySelectorAll("form.product-form").forEach((form) => {
      setField(form, "[data-pf-ref]", state.token);
      setField(form, "[data-pf-claim]", state.claimId || "");
      setField(form, "[data-pf-attr-v]", state.attributionVersion || ATTR_V);
      setField(form, "[data-pf-captured-at]", state.capturedAt || "");
    });
  }

  function syncCart(state) {
    if (!state?.token || typeof root.fetch !== "function") return;
    const body = JSON.stringify({
      attributes: {
        pf_ref: state.token,
        pf_claim: state.claimId || "",
        pf_attr_v: state.attributionVersion || ATTR_V,
        pf_captured_at: state.capturedAt || "",
      },
    });
    root.fetch("/cart/update.js", { method: "POST", headers: { "Content-Type": "application/json" }, body }).catch(() => {});
  }

  function diagnostic(name, props) {
    try {
      if (root.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() === false) return;
      root.posthog?.capture?.(name, props || {});
    } catch (_) {}
  }

  // The Worker's own answer that it doesn't know this token (410, or 404 with its "Unknown referral" error) is not an
  // outage: the token is taken back off everything it was put on (REVIEW-R1-REFERRAL F3). A network error, a 5xx or any
  // other 404 (a Worker without this route answers {"error":"Not found"}) is still an outage, and fail-open keeps it.
  const UNKNOWN = "unknown";
  async function claim(token, prior) {
    const response = await root.fetch(`${workerOrigin()}/v1/referrals/claim`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ token, claimId: prior?.claimId || undefined }),
    });
    if (response.status === 404 || response.status === 410) {
      let answer = null;
      try { answer = await response.json(); } catch (_) {}
      if (response.status === 410 || /referral/i.test(String(answer?.error || ""))) throw new Error(UNKNOWN);
    }
    if (!response.ok) throw new Error("claim");
    return response.json();
  }

  function forget() {
    try { root.localStorage?.removeItem(KEY); } catch (_) {}
    root.document.querySelectorAll("form.product-form").forEach((form) => {
      ["[data-pf-ref]", "[data-pf-claim]", "[data-pf-attr-v]", "[data-pf-captured-at]"].forEach((selector) => setField(form, selector, ""));
    });
    if (typeof root.fetch !== "function") return;
    // A cart attribute set to an empty value is removed.
    const body = JSON.stringify({ attributes: { pf_ref: "", pf_claim: "", pf_attr_v: "", pf_captured_at: "" } });
    root.fetch("/cart/update.js", { method: "POST", headers: { "Content-Type": "application/json" }, body }).catch(() => {});
  }

  async function boot() {
    const fromUrl = readToken();
    let state = fromUrl ? { token: fromUrl, claimId: "", capturedAt: new Date().toISOString(), attributionVersion: ATTR_V } : load();
    if (!state?.token) return;
    applyToForms(state);
    persist(state);
    try {
      const claimed = await claim(state.token, state);
      if (claimed?.claimId) {
        state = {
          token: claimed.token || state.token,
          claimId: claimed.claimId,
          capturedAt: claimed.capturedAt || state.capturedAt,
          attributionVersion: claimed.attributionVersion || ATTR_V,
        };
        persist(state);
        applyToForms(state);
      }
    } catch (error) {
      if (error?.message === UNKNOWN) {
        forget();
        diagnostic("pf_referral_claim", { outcome: "unknown" });
        return;
      }
      // Fail-open: keep the registered token on the form even if the Worker is down.
    }
    syncCart(state);
    diagnostic("pf_referral_claim", { outcome: state.claimId ? "success" : "started" });
  }

  const api = { readToken, load, applyToForms, boot };
  if (typeof module !== "undefined" && module.exports) module.exports = api;
  else root.PocketReferral = api;

  if (typeof document === "undefined") return;
  // A deferred script runs before pocket-core.js; DOMContentLoaded comes after every deferred script.
  const early = document.readyState === "loading" || (document.readyState === "interactive" && !root.PocketCore);
  if (early) document.addEventListener("DOMContentLoaded", boot, { once: true });
  else boot();
})(typeof window !== "undefined" ? window : globalThis);
